import React, { useEffect, useState, useRef } from "react";
import { Alert, Form } from "react-bootstrap";
import { Download, FileText } from "lucide-react";
import { apiBase, dateLabel, ReviewApiError } from "./api";
import ReviewMarkdown from "./ReviewMarkdown";
import { Divider } from "./AttorneyWorkspace";
import SubmittedDocument from "./SubmittedDocument";
import "./submittedRecord.css";

/** Everything the client provided: submitted documents and files, then files sent later on request. */
export function submittedItems(snapshot: any, addedFiles: any[] = []): any[] {
  const documents = snapshot.documents || [];
  const attached = new Set(documents.flatMap((d: any) => d.files || []));
  return [
    ...documents.map((document: any) => ({
      id: `document:${document.id}`,
      name: document.name,
      document,
    })),
    ...(snapshot.files || [])
      .filter((file: any) => !attached.has(file.id))
      .map((file: any) => ({ id: `file:${file.id}`, name: file.name, file })),
    ...addedFiles.map((file: any) => ({
      id: `file:${file.id}`,
      name: `${file.name} (added after submission)`,
      file,
      added: true,
    })),
  ];
}

function CapturedFilePreview({
  reviewId,
  file,
  onAccessError,
}: {
  reviewId: string;
  file: any;
  onAccessError: (error: any) => void;
}) {
  const [preview, setPreview] = useState<{
    kind: string;
    content: string;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    async function load() {
      const response = await fetch(
        `${apiBase}/api/reviews/${reviewId}/files/${file.id}`,
        {
          credentials: "include",
          signal: controller.signal,
        }
      );
      if (!response.ok)
        throw new ReviewApiError(
          response.status,
          "The submitted file could not be opened. Refresh the review and try again."
        );
      const bytes = await response.arrayBuffer();
      if (controller.signal.aborted) return;
      const type = (file.contentType || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      if (type === "application/pdf" || name.endsWith(".pdf")) {
        objectUrl = URL.createObjectURL(
          new Blob([bytes], { type: "application/pdf" })
        );
        setPreview({ kind: "pdf", content: objectUrl });
      } else if (
        /^image\/(png|jpeg|gif|webp)$/.test(type) ||
        /\.(png|jpe?g|gif|webp)$/.test(name)
      ) {
        const imageType = /^image\/(png|jpeg|gif|webp)$/.test(type)
          ? type
          : name.endsWith(".png")
            ? "image/png"
            : /\.jpe?g$/.test(name)
              ? "image/jpeg"
              : name.endsWith(".gif")
                ? "image/gif"
                : "image/webp";
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: imageType }));
        setPreview({ kind: "image", content: objectUrl });
      } else if (name.endsWith(".docx") || type.includes("wordprocessingml")) {
        const mammoth = await import("mammoth");
        const result = await mammoth.convertToHtml({ arrayBuffer: bytes });
        if (!controller.signal.aborted)
          setPreview({ kind: "html", content: result.value });
      } else if (
        type.startsWith("text/") ||
        /\.(txt|md|csv|json)$/.test(name) ||
        type === "application/json"
      ) {
        const text = new TextDecoder().decode(bytes);
        setPreview({ kind: "text", content: text });
      } else {
        throw new Error(
          "This file format does not support an inline preview. The captured original is available below."
        );
      }
    }
    load().catch(e => {
      if (controller.signal.aborted) return;
      if (
        e instanceof ReviewApiError &&
        [401, 403, 404, 503].includes(e.status)
      )
        onAccessError(e);
      else setError(e.message || "Unable to preview this file.");
    });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reviewId, file.id, file.name, file.contentType, onAccessError]);
  if (error) return <Alert variant="info">{error}</Alert>;
  if (!preview)
    return (
      <p className="submitted-preview-loading" role="status">
        Opening submitted document…
      </p>
    );
  if (preview.kind === "pdf")
    return (
      <iframe
        className="submitted-pdf"
        title={`Submitted PDF: ${file.name}`}
        src={`${preview.content}#view=FitH`}
      />
    );
  if (preview.kind === "image")
    return (
      <img className="submitted-image" src={preview.content} alt={file.name} />
    );
  return (
    <SubmittedDocument
      html={preview.kind === "html" ? preview.content : undefined}
      text={preview.content}
    />
  );
}

function DocumentPreview({
  reviewId,
  item,
  files,
  onAccessError,
}: {
  reviewId: string;
  item: any;
  files: Map<string, any>;
  onAccessError: (error: any) => void;
}) {
  const doc = item.document;
  const attachments = doc
    ? (doc.files || []).map((id: string) => files.get(id)).filter(Boolean)
    : [item.file];
  const pdf = attachments.find(
    (f: any) =>
      f.contentType === "application/pdf" ||
      f.name?.toLowerCase().endsWith(".pdf")
  );
  const original =
    attachments.find((f: any) => f.contentType !== "application/json") ||
    attachments[0];
  const file =
    item.file ||
    ((!doc.html && !doc.text) || doc.type === "pdf"
      ? pdf || original
      : undefined);
  return (
    <>
      <div
        className={`submitted-document-content${file?.contentType === "application/pdf" || file?.name?.toLowerCase().endsWith(".pdf") ? " is-pdf" : ""}`}
        tabIndex={0}
        aria-label="Submitted document content"
      >
        {file ? (
          <CapturedFilePreview
            key={file.id}
            reviewId={reviewId}
            file={file}
            onAccessError={onAccessError}
          />
        ) : (
          <div className="submitted-document-paper">
            <SubmittedDocument html={doc.html} text={doc.text} />
          </div>
        )}
        {!!doc?.comments?.length && (
          <details className="submitted-comments">
            <summary>Saved comments ({doc.comments.length})</summary>
            {doc.comments.map((comment: any, index: number) => (
              <p key={comment.id || index}>
                {comment.text ||
                  comment.body ||
                  "Comment preserved in the submitted file."}
              </p>
            ))}
          </details>
        )}
      </div>
      {!!attachments.length && (
        <details className="submitted-source-files">
          <summary>Original files and downloads</summary>
          {attachments.map((f: any) => (
            <a
              className="review-file"
              key={f.id}
              href={`${apiBase}/api/reviews/${reviewId}/files/${f.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <Download size={13} />
              {f.name}
            </a>
          ))}
        </details>
      )}
    </>
  );
}

/** Drops tracking parameters (utm_*) the AI adds to links. */
export function cleanSourceUrl(raw: string) {
  try {
    const url = new URL(raw);
    Array.from(url.searchParams.keys())
      .filter(key => key.startsWith("utm_"))
      .forEach(key => url.searchParams.delete(key));
    return url.toString();
  } catch {
    return raw;
  }
}
/** "law.justia.com › cases › california › court-of-appeal › 2d › 73 › 103.html" */
export function sourceLabel(raw: string) {
  try {
    const url = new URL(raw);
    const path = url.pathname
      .split("/")
      .filter(Boolean)
      .map(decodeURIComponent);
    return [url.hostname.replace(/^www\./, ""), ...path].join(" › ");
  } catch {
    return raw;
  }
}
export default function SubmittedRecord({
  reviewId,
  snapshot,
  hash,
  embedded = false,
  onAccessError,
  onQuote,
  onPin,
  initialDocumentId,
  clientView = false,
  addedFiles = [],
}: {
  reviewId: string;
  snapshot: any;
  hash: string;
  embedded?: boolean;
  /** Insert a pinned passage into the answer, with a readable source. */
  onQuote?: (text: string, source: string) => void;
  /** Reports the pinned passage so the answer editor can cite it. */
  onPin?: (pin: { text: string; source: string } | null) => void;
  /** Files the client sent later because the attorney asked for them. */
  addedFiles?: any[];
  initialDocumentId?: string;
  onAccessError: (error: any) => void;
  /** The customer reading their own submission: no attorney tools. */
  clientView?: boolean;
}) {
  const [selected, setSelected] = useState(initialDocumentId || "");
  useEffect(() => {
    if (initialDocumentId) setSelected(initialDocumentId);
  }, [initialDocumentId]);
  const [pinnedDocument, setPinnedDocument] = useState("");
  const [split, setSplit] = useState(43),
    [pinned, setPinned] = useState(""),
    [pinnedTarget, setPinnedTarget] = useState("");
  const root = useRef<HTMLElement>(null);
  const allMessages = (snapshot.conversations || []).flatMap(
    (c: any, i: number) =>
      c.messages.map((m: any, n: number) => ({
        ...m,
        anchor: `source-${reviewId}-${i}-${n}`,
      }))
  );
  const sources = Array.from(
    new Set<string>(
      allMessages.flatMap((m: any) =>
        (m.content.match(/https?:\/\/[^\s<>)\]]+/g) || []).map(cleanSourceUrl)
      )
    )
  );
  const items = submittedItems(snapshot, addedFiles);
  // "the client's message 1" / "the AI's answer, message 2" / "Contract (document)"
  const sourceFor = (target: string, documentId: string) => {
    if (documentId) {
      const doc = items.find(i => i.id === documentId);
      return doc ? `${doc.name} (submitted document)` : "Submitted document";
    }
    const index = allMessages.findIndex((m: any) => m.anchor === target);
    if (index < 0) return "Client's materials";
    return allMessages[index].role === "user"
      ? `Client's message ${index + 1}`
      : `AI answer, message ${index + 1}`;
  };
  const pinnedSource = pinned ? sourceFor(pinnedTarget, pinnedDocument) : "";
  useEffect(() => {
    onPin?.(pinned ? { text: pinned, source: pinnedSource } : null);
  }, [onPin, pinned, pinnedSource]);
  const item = items.find(i => i.id === selected) || items[0];
  const files = new Map<string, any>(
    (snapshot.files || []).map((f: any) => [f.id, f])
  );
  const viewFile = (id: string) => {
    const match = items.find(
      i => i.file?.id === id || i.document?.files?.includes(id)
    );
    if (match) setSelected(match.id);
  };
  return (
    <section
      ref={root}
      id={`submitted-record-${reviewId}`}
      className={`review-section submitted-record${items.length ? " has-documents" : ""}${embedded ? " is-embedded" : ""}`}
    >
      {!embedded && <h2>Submitted record</h2>}
      {snapshot.beta && <details className="reviewed-note mb-3"><summary>Submitted parties and test-review notice</summary><p><strong>People or companies involved:</strong> {snapshot.beta.parties}</p><p>{snapshot.beta.reviewNotice?.text}</p><small>{snapshot.beta.reviewNotice?.version}</small></details>}
      <section
        id={`review-question-${reviewId}`}
        data-review-reference
        className="submitted-review-request"
        aria-label={clientView ? "Your request" : "Client’s review question"}
      >
        <h3>{clientView ? "Your request" : "Client’s review question"}</h3>
        <p>
          {snapshot.reviewRequest ||
            "No review question was recorded for this earlier submission."}
        </p>
      </section>
      <div className="record-navigation">
        {!clientView && (
          <Form.Select
            size="sm"
            aria-label="Jump to submitted message"
            defaultValue=""
            onChange={e =>
              document
                .getElementById(e.target.value)
                ?.scrollIntoView({ block: "start" })
            }
          >
            <option value="" disabled>
              Jump to a message…
            </option>
            {allMessages.map((m: any, i: number) => (
              <option key={m.anchor} value={m.anchor}>
                {i + 1}. {m.role === "user" ? "Client" : "AI"}:{" "}
                {m.content.slice(0, 75)}
              </option>
            ))}
          </Form.Select>
        )}
        {!clientView && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => {
              const selection = window.getSelection();
              if (selection && root.current?.contains(selection.anchorNode)) {
                setPinned(selection.toString().slice(0, 2000));
                const parent = selection.anchorNode?.parentElement;
                setPinnedDocument(
                  parent?.closest(".submitted-document-pane")
                    ? item?.id || ""
                    : ""
                );
                setPinnedTarget(
                  parent?.closest("[data-review-reference]")?.id ||
                    `submitted-record-${reviewId}`
                );
              }
            }}
          >
            Pin selected passage
          </button>
        )}
        {!!sources.length && (
          <details>
            <summary>AI's sources ({sources.length})</summary>
            <ul>
              {sources.map(url => (
                <li key={url}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={url}
                  >
                    {sourceLabel(url)}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {pinned && (
        <aside className="pinned-passage" aria-label="Pinned passage">
          <blockquote>{pinned}</blockquote>
          {onQuote && (
            <button type="button" onClick={() => onQuote(pinned, pinnedSource)}>
              Insert in answer
            </button>
          )}
          <button type="button" onClick={() => setPinned("")}>
            Unpin
          </button>
        </aside>
      )}
      <div
        className="submitted-record-body"
        style={
          item
            ? {
                gridTemplateColumns: `minmax(0,${split}fr) 10px minmax(0,${100 - split}fr)`,
              }
            : undefined
        }
      >
        <div
          className="submitted-conversation-scroll"
          tabIndex={0}
          aria-label="Submitted conversation"
        >
          {snapshot.conversations.map((c: any, i: number) => (
            <section key={c.chatId || i}>
              <h3 className="h5">
                AI conversation
                {snapshot.conversations.length > 1 ? ` ${i + 1}` : ""}
              </h3>
              <div className="review-transcript">
                {c.messages.map((m: any, n: number) => (
                  <article
                    className={`review-chat-message ${m.role === "user" ? "client" : "ai"}`}
                    key={m.id || n}
                    id={`source-${reviewId}-${i}-${n}`}
                    data-review-reference
                  >
                    <header>
                      <strong>
                        {m.role === "user"
                          ? "Client"
                          : "AI assistant · not attorney reviewed"}
                      </strong>
                    </header>
                    <ReviewMarkdown>{m.content}</ReviewMarkdown>
                    <a
                      className="source-message-anchor"
                      href={`?section=snapshot&target=source-${reviewId}-${i}-${n}`}
                    >
                      Message {n + 1}
                    </a>
                    {m.files?.map(
                      (id: string) =>
                        files.has(id) && (
                          <button
                            type="button"
                            className="submitted-view-file"
                            key={id}
                            onClick={() => viewFile(id)}
                          >
                            <FileText size={13} />
                            View {files.get(id).name}
                          </button>
                        )
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
          <details className="submitted-record-details">
            <summary>Submission details</summary>
            <p>
              Captured {dateLabel(snapshot.capturedAt)}. Later AI messages and
              document edits are not included.
            </p>
            {snapshot.engagement && (
              <details>
                <summary>
                  {snapshot.engagement.test_only
                    ? "Accepted local test notice"
                    : "Engagement acceptance"}
                </summary>
                <p>
                  Version {snapshot.engagement.terms_version} · accepted{" "}
                  {dateLabel(snapshot.engagement.accepted_at)}
                </p>
                <div className="review-prose">
                  {snapshot.engagement.terms_text}
                </div>
              </details>
            )}
            <details>
              <summary>Submission integrity</summary>
              <p className="review-hash">SHA-256: {hash}</p>
            </details>
          </details>
        </div>
        {item && (
          <Divider
            name="Resize conversation and document"
            value={split}
            axis="x"
            range={[25, 75]}
            controls={`document-pane-${reviewId}`}
            onChange={value => setSplit(Math.max(25, Math.min(75, value)))}
          />
        )}
        {item && (
          <section
            id={`document-pane-${reviewId}`}
            data-review-reference
            className="submitted-document-pane"
            aria-label="Submitted documents"
          >
            <header className="submitted-document-toolbar">
              <label htmlFor={`submitted-document-${reviewId}`}>
                <FileText size={14} /> Submitted document
                {items.length > 1 ? "s" : ""}
              </label>
              {items.length > 1 ? (
                <Form.Select
                  id={`submitted-document-${reviewId}`}
                  value={item.id}
                  onChange={e => setSelected(e.target.value)}
                >
                  {items.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                      {i.document ? ` · version ${i.document.version}` : ""}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <strong>
                  {item.name}
                  {item.document ? ` · version ${item.document.version}` : ""}
                </strong>
              )}
            </header>
            <DocumentPreview
              key={item.id}
              reviewId={reviewId}
              item={item}
              files={files}
              onAccessError={onAccessError}
            />
          </section>
        )}
      </div>
    </section>
  );
}
